// The landing hands a visitor's sentence to the workspace as `?prompt=<text>`
// (company/packages/landing's PromptBar.js + the hero composer's
// RevealEndCard.js — both `window.location.href =
// 'https://my.symbols.app/?prompt=' + encodeURIComponent(sentence)`, landing's
// side of WS-WORKSPACE-CONSUMES-THE-PROMPT-QUERY-FROM-THE-LANDING-HERO-
// COMPOSER-1). `prompt` is the coordinated param name — a rename needs both
// sides. Framework-free (no `window`/DOMQL reference) so it unit-tests with
// no DOM shim; the shell-root CanvasPromptTextarea mount (app.js) is the only
// caller.
export const INBOUND_PROMPT_PARAM = 'prompt'
export const INBOUND_PROMPT_MAX_LENGTH = 2000

// `URLSearchParams` already percent-decodes the value — a second
// `decodeURIComponent` pass would mangle a literal '%' the visitor typed
// (e.g. "50% off"). Trimmed, then cut to INBOUND_PROMPT_MAX_LENGTH. Returns
// null for an absent/blank param so callers can `if (!text) return` without
// re-checking emptiness. Whatever the text contains — including a literal
// `<script>` — this returns it verbatim as a STRING; staying inert is the
// caller's job (bind it to a textarea's `value`/state, never `innerHTML`).
export const parseInboundPrompt = (search) => {
  const raw = new URLSearchParams(search || '').get(INBOUND_PROMPT_PARAM)
  if (!raw) return null
  const text = raw.trim().slice(0, INBOUND_PROMPT_MAX_LENGTH)
  return text || null
}

// A `?prompt=` on the APP BUILDER's route belongs to the builder: it opens a
// new build thread with it as the first message (functions/addAppPrompt.js —
// an inbound link: an external one, an old bookmark, Fly's older
// `/w/<org>/<ws>/add-app?prompt=…&fly=` hand-over; the welcome parks its idea
// and sends none), so the home composer never also takes it. `path` is the raw
// pathname: the workspace prefix (`/w/<org>` or `/w/<org>/<ws>`) is read here,
// because at boot the session may not know the workspace handle yet.
export const inboundPromptForBuilder = (path) =>
  /^(\/w\/[a-z0-9-]{1,48}(\/[A-Za-z0-9_-]{1,64})?)?\/add-app(\/|$)/.test(
    String(path || '').split(/[?#]/)[0]
  )

// Address-bar cleanup after the composer consumes the param, so a reload
// never re-prefills. Drops ONLY `prompt` — every other query param and the
// hash survive untouched.
export const stripInboundPromptParam = (href) => {
  const url = new URL(href)
  url.searchParams.delete(INBOUND_PROMPT_PARAM)
  return url.toString()
}

// The park — a prompt that arrives BEFORE the composer exists.
//
// Measured on the signed-in dev.my.symbols.app seat, 2026-09-23, with an
// instrumented iframe boot: the shell root's hand-off block runs at t≈12.91 s
// and the composer's textarea mounts at t≈13.63 s — 716 ms LATER. So
// `root._prefillHomePrompt?.(text)` called a bridge that CanvasPromptTextarea
// had not registered yet (a silent no-op, the `?.`), while the address-bar
// strip above it had already deleted the only copy of the sentence. The
// visitor landed on an empty composer, which is the defect this ticket names.
//
// The park is the acceptance's own second option ("the text is parked in
// sessionStorage before the redirect and restored after"). sessionStorage,
// not localStorage: the hand-off belongs to THIS tab, and a second tab must
// never inherit a stranger's sentence. One-shot (the read removes it) and
// short-lived, so a reload never prefills twice.
export const INBOUND_PROMPT_PARK_KEY = 'symbols_inbound_prompt'
export const INBOUND_PROMPT_PARK_MAX_AGE_MS = 10 * 60 * 1000

// `storage` is injected (a Storage, or any {getItem,setItem,removeItem}) so
// this file stays framework-free and unit-tests with no DOM shim. Returns
// whether the text is now held somewhere the composer can still reach it —
// the caller strips the address bar only on a `true`.
export const parkInboundPrompt = (text, storage, now = Date.now()) => {
  if (!text || !storage) return false
  try {
    storage.setItem(
      INBOUND_PROMPT_PARK_KEY,
      JSON.stringify({ text: String(text).slice(0, INBOUND_PROMPT_MAX_LENGTH), at: now })
    )
    return true
  } catch (_) {
    // A sandboxed frame or a full quota — the sentence is not held, and the
    // caller keeps `?prompt=` in the address bar rather than eating it.
    return false
  }
}

// Read AND remove, in one call: the park is consumed whatever its content
// turns out to be, so a torn or stale row can never prefill a later load.
export const takeParkedInboundPrompt = (storage, now = Date.now()) => {
  if (!storage) return null
  let raw = null
  try {
    raw = storage.getItem(INBOUND_PROMPT_PARK_KEY)
    if (raw) storage.removeItem(INBOUND_PROMPT_PARK_KEY)
  } catch (_) {
    return null
  }
  if (!raw) return null
  let parked = null
  try {
    parked = JSON.parse(raw)
  } catch (_) {
    return null
  }
  if (!parked || typeof parked.text !== 'string' || !parked.at) return null
  if (now - Number(parked.at) > INBOUND_PROMPT_PARK_MAX_AGE_MS) return null
  const text = parked.text.trim().slice(0, INBOUND_PROMPT_MAX_LENGTH)
  return text || null
}

// The whole hand-off, in the order that cannot lose the sentence: read it,
// offer it to the composer bridge, park it when the bridge is not there yet
// (or refuses it), and only THEN hand back the stripped address-bar URL.
// `deliver` must return `true` to claim the text — anything else counts as
// not delivered, so a bridge that throws or returns undefined parks instead
// of dropping. `nextHref` is null when the text is neither delivered nor
// parked: keeping `?prompt=` visible is the honest fallback, since the next
// load can still read it.
export const receiveInboundPrompt = ({ search, href, deliver, storage, now = Date.now() } = {}) => {
  const text = parseInboundPrompt(search)
  if (!text) return { text: null, delivered: false, parked: false, nextHref: null }
  let delivered = false
  try {
    delivered = typeof deliver === 'function' && deliver(text) === true
  } catch (_) {
    delivered = false
  }
  const parked = delivered ? false : parkInboundPrompt(text, storage, now)
  const held = delivered || parked
  return {
    text,
    delivered,
    parked,
    nextHref: held && typeof href === 'string' && href ? stripInboundPromptParam(href) : null
  }
}
